import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../AppStores";
import type {TransformObj} from "../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import type {IFieldAdapter, IFieldPointer} from "../../../../data/models/field/Field";
import type {IModel} from "../../../../data/models/Model";
import {FieldDataType, XyiconFeature} from "../../../../generated/api/base";
import {
	getTooltipByIcon,
	type FieldIconName,
	getIconComponentFromFieldIconName,
} from "../../../modules/abstract/sidepanel/tabs/details/field/FieldInputUtils";
import type {AppState} from "../../../../data/state/AppState";
import type {TransportLayer} from "../../../../data/TransportLayer";
import {TimeUtils} from "../../../../utils/TimeUtils";
import type {IFieldPropagation, IXyiconLinkObject} from "../../../../data/state/AppActions";
import type {Link} from "../../../../data/models/Link";
import type {Boundary} from "../../../../data/models/Boundary";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {XHRLoader} from "../../../../utils/loader/XHRLoader";
import type {Xyicon} from "../../../../data/models/Xyicon";
import InfoIcon from "../../icons/circle-information.svg?react";
import UnlinkIcon from "../../icons/unlink.svg?react";
import PinIcon from "../../icons/location-pin.svg?react";
import {FieldV5} from "../FieldV5";
import type {IMultiSelectOption} from "../../../../data/models/field/datatypes/MultiSelect";
import {CardLayoutToolTipV5} from "../../topbar/CardLayoutTooltipV5";
import {getOrgSearchModelsByIds} from "../../topbar/OrganizationWideSearchUtils";
import {PropagatedFieldInDetailsContent} from "./SingleFieldInput.styled";
import {FieldInputV5} from "./FieldInputV5";

interface ISingleFieldInputState {
	isToolTipOpen: boolean;
	toolTipTransform: TransformObj;
	itemIndex: number;
}

interface ISingleFieldInputProps<T extends IModel> {
	readonly item: T;
	readonly field: IFieldAdapter;
	readonly fieldRefIdsForType: IFieldPointer[];
	readonly noWrap: boolean;
	readonly feature: XyiconFeature;
	readonly disabled?: boolean;
	readonly icon?: FieldIconName;
	readonly className?: string;
	readonly isXyiconXyiconLink?: boolean;
	readonly isInheritedFromBoundary?: boolean;
	readonly onSelectPropagatedSource: (model: IModel) => void;
	readonly closeWideSearchPanel?: () => void;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

@withAppStores("appState", "transport")
@observer
export class SingleFieldInputV5<T extends IModel> extends React.Component<ISingleFieldInputProps<T>, ISingleFieldInputState> {
	private _fieldInputRef = React.createRef<FieldInputV5>();

	private _cardLayoutToolTipRef = React.createRef<HTMLDivElement>();
	private parentRefArray: HTMLDivElement[] = [];

	constructor(props: ISingleFieldInputProps<T>) {
		super(props);
		this.state = {
			isToolTipOpen: false,
			toolTipTransform: null,
			itemIndex: -1,
		};
	}

	private readonly onInputChange = (value: string | string[] | number | IMultiSelectOption[]) => {
		const {item, field} = this.props;
		const {actions} = this.props.appState;

		return TimeUtils.waitUpdate(
			actions.updateFields([item], {
				[field.refId]: value,
			}),
			this.props.appState.app.notificationContainer,
		);
	};

	private getLinkMaybe(propagation: IFieldPropagation) {
		let links: ({link: Link; object: Boundary} | IXyiconLinkObject)[] = [];

		if (typeof propagation.modelOrId === "string") {
			return null;
		} else {
			if (propagation.modelOrId.ownFeature === XyiconFeature.Xyicon && this.props.item.ownFeature === XyiconFeature.Xyicon) {
				links = this.props.appState.actions.getLinksXyiconXyicon(this.props.item.id);
			} else if (propagation.modelOrId.ownFeature === XyiconFeature.Boundary && this.props.item.ownFeature === XyiconFeature.Boundary) {
				links = this.props.appState.actions.getLinksBoundaryBoundary(this.props.item.id);
			} else {
				links = this.props.appState.actions.getLinksXyiconBoundary(this.props.item.id);
			}

			return links.find((l) => l.object.refId === (propagation.modelOrId as IModel).refId);
		}
	}

	private onDetailsClick = async (propagation: IFieldPropagation) => {
		if (typeof propagation.modelOrId === "string") {
			const models = await getOrgSearchModelsByIds(this.props.appState, [propagation.modelOrId]);

			this.props.appState.app.onDetailsClick?.(models[0]);
		} else {
			this.props.appState.app.onDetailsClick?.(propagation.modelOrId);
		}
	};

	private onPinClick = (propagation: IFieldPropagation) => {
		const linkMaybe = this.getLinkMaybe(propagation);

		if (linkMaybe) {
			this.props.appState.actions.navigateToSpaceItem(linkMaybe.object, true);
			this.props.closeWideSearchPanel?.();
		}
	};

	private onBreakLinkClick = async (propagation: IFieldPropagation) => {
		const {transport} = this.props;
		const linkMaybe = this.getLinkMaybe(propagation);

		if (linkMaybe) {
			const portfolioId = this.props.item.portfolioId ?? transport.appState.activePortfolioId;

			await transport.requestForOrganization({
				url: "xyicons/deletelink",
				method: XHRLoader.METHOD_DELETE,
				params: {
					portfolioID: portfolioId,
					linkIDList: [linkMaybe.link.id],
				},
			});
		}
	};

	private onMouseLeave = () => {
		this.setState({itemIndex: -1, isToolTipOpen: false});
	};

	public override UNSAFE_componentWillReceiveProps(nextProps: ISingleFieldInputProps<T>) {
		if (nextProps.item !== this.props.item) {
			// TODO V5
			// const clickToEditRef = this._fieldInputRef.current?.clickToEditRef.current;
			// const editingValue = clickToEditRef?.editingValue;
			// if (clickToEditRef?.state.editing)
			// {
			// 	this.onInputChange?.(editingValue);
			// 	clickToEditRef?.cancelLocalEdit();
			// }
		}
	}

	public override componentDidUpdate(prevProps: ISingleFieldInputProps<T>, prevState: ISingleFieldInputState) {
		const isFieldMultiSelect = this.props.field.dataType === FieldDataType.MultipleChoiceList;

		if (!prevState.isToolTipOpen && this.state.isToolTipOpen && this.parentRefArray[this.state.itemIndex] && this._cardLayoutToolTipRef.current) {
			this.setState({
				toolTipTransform: DomUtils.getFixedFloatingElementPosition(
					this.parentRefArray[this.state.itemIndex],
					this._cardLayoutToolTipRef.current,
					isFieldMultiSelect ? VerticalAlignment.center : VerticalAlignment.topOuter,
					isFieldMultiSelect ? HorizontalAlignment.outerLeft : HorizontalAlignment.center,
				),
			});
		}
	}

	public override render() {
		const {
			item,
			field,
			fieldRefIdsForType,
			noWrap,
			feature,
			appState,
			disabled,
			icon,
			className,
			onSelectPropagatedSource,
			isInheritedFromBoundary,
			isXyiconXyiconLink,
		} = this.props;
		const {isToolTipOpen, toolTipTransform, itemIndex} = this.state;
		const actions = appState.actions;

		const value = actions.getOwnFieldValue(item, field.refId);

		const isUpdating = appState.itemFieldUpdateManager.isItemFieldBeingUpdated(item.id, field.refId, feature);

		// Default fields and inherited fields are not editable
		const shouldBeDisabled = disabled || field.feature !== feature || field.default || field.hasFormula;
		const propagations = actions.getFieldPropagations(item, field);

		// Show input if feature is the same and type mapping matches or default
		// Note is there a way showInput will become false here?
		// Note for the above question: if the Input field is an inherited DOL field

		const showOwnInput =
			field.feature === feature && !actions.isFieldHiddenByMasking(item, field) && (fieldRefIdsForType.includes(field.refId) || field.default);

		const inlineStyle: React.CSSProperties = this._cardLayoutToolTipRef && {
			position: "absolute",
			top: 0,
			left: 0,
			transform: toolTipTransform?.translate,
		};

		return (
			<FieldV5
				key={`${field.refId}${item.id}`}
				label={field.name}
				disabled={shouldBeDisabled || isUpdating}
				noWrap={noWrap}
				className={`${className ?? ""} ${icon ?? ""}`}
				icons={{preLabelIcon: getIconComponentFromFieldIconName(icon)}}
				tooltips={{
					labelTooltip: field.helperText,
					preLabelIconTooltip: getTooltipByIcon(icon),
				}}
			>
				<>
					{/* //Please do not delete it, it is for debugging. It shows the field's refid. */}
					{/* <div style={{position: "absolute", top: "-3px", left: "-30px", fontSize: "10px", fontWeight: 700}}>{this.props.field.refId}, {shouldBeDisabled ? "⛔" : "✔"}</div> */}
					{showOwnInput && (
						<FieldInputV5
							field={field}
							ref={this._fieldInputRef}
							dataType={field.dataType}
							dataTypeSettings={field.dataTypeSettings}
							value={value}
							onChange={this.onInputChange}
							disabled={shouldBeDisabled}
							isUpdating={isUpdating}
							dropdownIcon={false}
							isInherited={false}
						/>
					)}
					{propagations?.map((propagation, index) => (
						<PropagatedFieldInDetailsContent
							key={`${field.fieldID || field.refId} ${index}`}
							ref={(parentRef) => {
								this.parentRefArray[index] = parentRef;
							}}
							onMouseOver={() => this.setState({itemIndex: index, isToolTipOpen: true})}
							onMouseLeave={this.onMouseLeave}
							$onlyFieldValue={!showOwnInput && index === 0}
						>
							<FieldInputV5
								field={actions.getFieldById(field.fieldID) || field}
								dataType={field.dataType}
								dataTypeSettings={field.dataTypeSettings}
								value={propagation.value}
								disabled={true}
								onClick={() => {
									if (typeof propagation.modelOrId === "object") {
										onSelectPropagatedSource(propagation.modelOrId);
									}
								}}
								dropdownIcon={false}
								isInherited={true}
							/>
							{icon && itemIndex === index && (
								<div className="buttonContainer hbox alignCenter">
									{isXyiconXyiconLink && typeof propagation.modelOrId === "object" && (
										<IconButtonV5
											IconComponent={UnlinkIcon}
											title="Break Link"
											onClick={() => this.onBreakLinkClick(propagation)}
										/>
									)}
									{(isXyiconXyiconLink || isInheritedFromBoundary) && (
										<IconButtonV5
											IconComponent={PinIcon}
											title="Open in Space Editor"
											onClick={() => this.onPinClick(propagation)}
											disabled={typeof propagation.modelOrId === "string" || (propagation.modelOrId as Xyicon).isUnplotted}
										/>
									)}
									<IconButtonV5
										IconComponent={InfoIcon}
										title="Details"
										onClick={() => this.onDetailsClick(propagation)}
									/>
								</div>
							)}
							{isToolTipOpen && itemIndex === index && typeof propagation.modelOrId === "object" && (
								<DomPortal destination={this.props.appState.app.modalContainer}>
									<CardLayoutToolTipV5
										item={propagation.modelOrId as Xyicon}
										divRef={this._cardLayoutToolTipRef}
										style={inlineStyle}
										className="PropagatedFieldCardLayout"
										fieldDataType={field.dataType}
									/>
								</DomPortal>
							)}
						</PropagatedFieldInDetailsContent>
					))}
				</>
			</FieldV5>
		);
	}
}
